// Check In / Check Out workspace.
//
// Three coordinated panels, all wired to the backend:
//
//   1. Quick walk-in — capture a guest who's standing at the desk right
//      now and put them in a room immediately (POST /reservations with
//      walk_in_now: true).
//
//   2. Expected today — every confirmed/pending reservation whose
//      check_in_date is today.  One click checks them in.
//
//   3. In house — every reservation with status `checked_in`.  Shows
//      balance + lets the front desk settle and check the guest out.
//      Marks the room "cleaning" so housekeeping picks it up.
//
// We refresh the lists after every mutation so the panels stay in sync.

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import {
  reservationsUrl,
  reservationCheckInUrl,
  reservationCheckOutUrl,
  reservationCancelUrl,
  roomsUrl,
  roomTypesUrl,
} from "../../BaseURL";
import {
  computeBillingClient,
  defaultStartForMode,
  endForMode,
} from "../../utils/billing";
import StayWindowPicker from "../../components/StayWindowPicker";
import MultiRoomEditor, { BLANK_ROOM_LINE } from "../../components/MultiRoomEditor";
import DepositHint, { useDepositGuard } from "../../components/DepositHint";
// Printable receipt — surfaced after walk-in deposit + after checkout
// settlement so the front desk doesn't have to hop to the Billing tab.
import { ReceiptDialog, openReceiptFor } from "../../components/Receipt";
import { useFacility } from "../../context/FacilityContext";
import { useNowTick } from "../../hooks/useNowTick";
import { beep, alertChime } from "../../utils/sound";

// Regular staff may cancel a check-in within this window; after that
// only a super_admin can.  Must match the server's CHECK_IN_CANCEL_WINDOW_MS.
const CANCEL_WINDOW_MS = 30 * 60 * 1000;
// When checkout is this close, start beeping every BEEP_STEP_MIN minutes.
const BEEP_WINDOW_MIN = 30;
const BEEP_STEPS_MIN  = [30, 25, 20, 15, 10, 5, 0];   // beep thresholds

const PAYMENT_METHODS = ["Cash", "Card", "Mobile Money", "Transfer"];
const BILLING_MODE_OPTIONS = [
  { id: "nightly", label: "Nightly",  hint: "Per midnight crossed" },
  { id: "hourly",  label: "Hourly",   hint: "Per hour (walk-ins / short)" },
  { id: "daily",   label: "Daily",    hint: "Per 24-hour block" },
];

const BLANK_WALK_IN = {
  full_name: "",
  phone: "",
  id_type: "Ghana Card",
  id_number: "",
  email: "",
  // Multi-room: one or more room lines per walk-in.  Same shape the
  // backend expects in the `rooms[]` payload.
  rooms: [{ ...BLANK_ROOM_LINE }],
  billing_mode:  "nightly",
  check_in_at:   "",
  check_out_at:  "",
  paid_amount: "",
  payment_method: "Cash",
  notes: "",
};

export default function CheckInOut() {
  const [reservations, setReservations] = useState([]);
  const [rooms, setRooms]    = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [walkIn, setWalkIn]  = useState(BLANK_WALK_IN);
  const [busy, setBusy]      = useState({}); // per-row busy flags
  const [msg, setMsg]        = useState({ type: "", text: "" });
  const [checkoutModal, setCheckoutModal] = useState(null);
  // Active receipt to render (filled by openReceiptFor / direct set).
  const [receipt, setReceipt] = useState(null);
  // "Print receipt" affordance shown next to the success toast — keeps
  // the operator in the same panel without forcing a tab switch.  Holds
  // a minimal reservation snapshot {id, code} so the openReceipt helper
  // can fetch the full detail at click time.
  const [receiptHint, setReceiptHint] = useState(null);
  const { facility } = useFacility();

  // Cached user — used for role-gated UI (super_admin can cancel a stay
  // even after the 30-minute window).
  const me = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); }
    catch (_) { return null; }
  }, []);
  const isSuperAdmin = me?.role === "super_admin";

  // One ticking clock for every countdown on the page.
  const now = useNowTick(1000);

  // Audible-alert bookkeeping: which (reservationId, threshold-min) pairs
  // we've already beeped for in this session.  Prevents double-beeping
  // and survives gentle re-renders.
  const beepedRef = useRef(new Set());

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4500);
  };

  // ── Loaders ───────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, roomRes, typeRes] = await Promise.all([
        axios.get(reservationsUrl),
        axios.get(roomsUrl),
        axios.get(roomTypesUrl),
      ]);
      setReservations(rRes.data?.results || []);
      setRooms(roomRes.data?.results || []);
      setRoomTypes(typeRes.data?.results || []);
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Could not load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Seed the walk-in form's default times so the StayWindowPicker boots
    // up already coherent with the current billing mode (nightly = today
    // 6 PM → tomorrow 7 AM, etc.).
    setWalkIn(w => {
      const mode  = w.billing_mode || "nightly";
      const start = defaultStartForMode(mode);
      return { ...w, check_in_at: start, check_out_at: endForMode(mode, start) };
    });
  }, [load]);

  // (Rate cascade now lives inside MultiRoomEditor — kept here as a
  // comment for parity with the Reservations modal which does the same.)

  // ── Derived lists ─────────────────────────────────────────────────────────
  const today = new Date().toISOString().slice(0, 10);

  const expectedArrivals = useMemo(
    () => reservations
      .filter(r => ['pending','confirmed'].includes(r.status))
      .sort((a, b) => String(a.check_in_at || a.check_in_date || "").localeCompare(String(b.check_in_at || b.check_in_date || ""))),
    [reservations]
  );

  const inHouse = useMemo(
    () => reservations
      .filter(r => r.status === 'checked_in')
      .sort((a, b) => String(a.check_out_at || a.check_out_date || "").localeCompare(String(b.check_out_at || b.check_out_date || ""))),
    [reservations]
  );

  // ── Audible alerts ──────────────────────────────────────────────────
  // For every in-house guest, when we cross a beep threshold (30 / 25 /
  // 20 / 15 / 10 / 5 / 0 minutes before checkout), play a tone.  The
  // 0-minute crossing plays a longer two-tone chime so the front desk
  // can't miss it.  Each (reservationId, threshold) combination only
  // fires once per session — beepedRef remembers what has already played.
  useEffect(() => {
    inHouse.forEach((r) => {
      const out = new Date(r.check_out_at || r.check_out_date);
      if (isNaN(out)) return;
      const minsLeft = (out.getTime() - now) / 60000;
      BEEP_STEPS_MIN.forEach((threshold) => {
        // Only beep when we've crossed below the threshold (within the
        // last second), not every tick after.  This is the "edge
        // detector" — we need minsLeft <= threshold AND the previous
        // tick was minsLeft > threshold.  Using the cache approximates
        // this: once a threshold is keyed, it never re-fires for this id.
        const key = `${r.id}:${threshold}`;
        if (!beepedRef.current.has(key) && minsLeft <= threshold && minsLeft > threshold - 1) {
          beepedRef.current.add(key);
          if (threshold === 0) alertChime();
          else                 beep({ frequency: 880, durationMs: 220 });
        }
      });
    });
  }, [now, inHouse]);

  const availableRooms = useMemo(
    () => rooms.filter(r =>
      r.is_active === 1 && ['available','cleaning','reserved'].includes(r.status)
    ),
    [rooms]
  );

  // ── Helpers ───────────────────────────────────────────────────────────────
  // Date + 24h time, used in rows so the front desk sees the precise
  // arrival / departure moment — especially important for hourly bookings.
  const fmtDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };
  const money = (n) => `GH₵ ${Number(n || 0).toFixed(2)}`;
  const isDueOut = (iso) => (iso || "").slice(0, 10) <= today;

  // Live billing preview — sums per-room totals so the grand total
  // updates as the operator adds rooms or tweaks rates.
  const billingPreview = useMemo(() => {
    const perRoom = (walkIn.rooms || []).map(line => computeBillingClient({
      billing_mode:   walkIn.billing_mode,
      check_in_at:    walkIn.check_in_at,
      check_out_at:   walkIn.check_out_at,
      rate_per_night: line.rate_per_night,
      rate_per_hour:  line.rate_per_hour,
      rate_per_day:   line.rate_per_day,
    }));
    const okAll = perRoom.every(b => b.ok);
    const total = perRoom.reduce((s, b) => s + (Number(b.total) || 0), 0);
    const first = perRoom[0] || { mode: walkIn.billing_mode };
    return {
      ok: okAll, total, mode: first.mode, perRoom,
      label: okAll
        ? (perRoom.length > 1
            ? `${perRoom.length} rooms · GH₵ ${total.toFixed(2)}`
            : (first.label || `GH₵ ${total.toFixed(2)}`))
        : "Enter dates to see total",
    };
  }, [walkIn.billing_mode, walkIn.check_in_at, walkIn.check_out_at, walkIn.rooms]);

  // ── Walk-in submit ───────────────────────────────────────────────────────
  // Deposit-policy guard — also enforced server-side; we mirror it here
  // so the operator gets immediate feedback and the Confirm button is
  // disabled until the rule is satisfied.
  const depositGuard = useDepositGuard({
    total: 0,                // recomputed below once billingPreview is known
    paid:  walkIn.paid_amount,
  });

  const submitWalkIn = async (e) => {
    e.preventDefault();
    if (!walkIn.full_name.trim()) { showMsg("error", "Guest name is required."); return; }
    if (!walkIn.check_in_at || !walkIn.check_out_at) {
      showMsg("error", "Both check-in and check-out times are required."); return;
    }
    const roomsPayload = (walkIn.rooms || [])
      .filter(line => line.room_id)
      .map(line => ({
        room_id: Number(line.room_id),
        adults:  Number(line.adults)   || 1,
        children: Number(line.children) || 0,
        rate_per_night: line.rate_per_night === "" || line.rate_per_night == null ? undefined : Number(line.rate_per_night),
        rate_per_hour:  line.rate_per_hour  === "" || line.rate_per_hour  == null ? undefined : Number(line.rate_per_hour),
        rate_per_day:   line.rate_per_day   === "" || line.rate_per_day   == null ? undefined : Number(line.rate_per_day),
      }));
    if (roomsPayload.length === 0) { showMsg("error", "Assign at least one room."); return; }

    setSubmitting(true);
    try {
      const payload = {
        guest: {
          full_name: walkIn.full_name,
          phone: walkIn.phone || null,
          email: walkIn.email || null,
          id_type: walkIn.id_type || null,
          id_number: walkIn.id_number || null,
        },
        rooms: roomsPayload,
        check_in_at:  walkIn.check_in_at,
        check_out_at: walkIn.check_out_at,
        billing_mode: walkIn.billing_mode,
        paid_amount:    walkIn.paid_amount    === "" ? 0 : Number(walkIn.paid_amount),
        payment_method: walkIn.payment_method,
        notes: walkIn.notes || undefined,
        source: 'walk_in',
        walk_in_now: true,
      };
      const res = await axios.post(reservationsUrl, payload);
      if (!res.data?.success) { showMsg("error", res.data?.message || "Check-in failed"); return; }
      const n = res.data.reservations?.length || 1;
      const grp = res.data.booking_group_code;
      showMsg("success", n > 1
        ? `${walkIn.full_name} checked into ${n} rooms · ${grp}`
        : `${walkIn.full_name} checked in (${res.data.code}).`);

      // Receipt hint — uses the primary reservation id returned by the
      // server (multi-room bookings are still printed per primary; the
      // shared receipt walks the payments ledger and we record one
      // deposit row in the staff POST handler).
      const primaryId   = res.data.id || res.data.reservations?.[0]?.id;
      const primaryCode = res.data.code || res.data.reservations?.[0]?.code;
      if (primaryId) {
        setReceiptHint({ id: primaryId, code: primaryCode || "—" });
      }
      // Reset the form to a fresh window for the SAME billing mode the
      // operator just used — back-to-back walk-ins of the same kind shouldn't
      // re-pick the mode every time.
      {
        const mode  = walkIn.billing_mode || "nightly";
        const start = defaultStartForMode(mode);
        setWalkIn({
          ...BLANK_WALK_IN,
          billing_mode: mode,
          check_in_at:  start,
          check_out_at: endForMode(mode, start),
        });
      }
      await load();
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Check-in failed");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Row actions ───────────────────────────────────────────────────────────
  const checkIn = async (r) => {
    setBusy(b => ({ ...b, [r.id]: true }));
    try {
      const res = await axios.patch(reservationCheckInUrl(r.id));
      if (!res.data?.success) { showMsg("error", res.data?.message || "Check-in failed"); return; }
      showMsg("success", `${r.guest_name} checked in.`);
      await load();
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Check-in failed");
    } finally {
      setBusy(b => { const c = { ...b }; delete c[r.id]; return c; });
    }
  };

  const openCheckout = (r) => {
    setCheckoutModal({
      reservation: r,
      payment_amount: r.balance > 0 ? String(r.balance) : "0",
      payment_method: r.payment_method || "Cash",
      notes: "",
    });
  };

  // Cancel a checked-in reservation.  Server enforces the 30-min /
  // super_admin rule — we just present the friendly confirm + relay any
  // permission error verbatim so the operator sees what's wrong.
  const cancelStay = async (r) => {
    if (!window.confirm(
      `Cancel check-in for ${r.guest_name} (${r.code})?\n\n` +
      `This will free Room ${r.room_number || "—"} and mark the reservation cancelled.`
    )) return;
    setBusy(b => ({ ...b, [r.id]: true }));
    try {
      const res = await axios.patch(reservationCancelUrl(r.id), {
        reason: "Cancelled from in-house panel",
      });
      if (!res.data?.success) {
        showMsg("error", res.data?.message || "Cancel failed");
        return;
      }
      showMsg("success", `${r.guest_name}'s stay cancelled.`);
      await load();
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Cancel failed");
    } finally {
      setBusy(b => { const c = { ...b }; delete c[r.id]; return c; });
    }
  };

  const confirmCheckout = async () => {
    if (!checkoutModal) return;
    const { reservation, payment_amount, payment_method, notes } = checkoutModal;
    setBusy(b => ({ ...b, [reservation.id]: true }));
    try {
      const payload = { notes: notes || null };
      const amt = Number(payment_amount) || 0;
      if (amt > 0) {
        payload.payment_amount = amt;
        payload.payment_method = payment_method;
      }
      const res = await axios.patch(reservationCheckOutUrl(reservation.id), payload);
      if (!res.data?.success) { showMsg("error", res.data?.message || "Checkout failed"); return; }
      showMsg("success", `${reservation.guest_name} checked out. Balance: ${money(res.data.balance)}.`);
      setCheckoutModal(null);
      // Auto-open the receipt — checkout is the natural moment for a
      // printable final bill, even when no balance was settled (the
      // recap of nights × rate is still useful as a take-away).
      openReceiptFor({ id: reservation.id }, setReceipt).catch(() => {});
      await load();
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Checkout failed");
    } finally {
      setBusy(b => { const c = { ...b }; delete c[reservation.id]; return c; });
    }
  };

  const todayLabel = new Date().toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

  // ── Reusable card pieces ──────────────────────────────────────────────────
  // Each panel is flex-column so its header stays pinned while the body
  // scrolls independently — no more dragging one list and accidentally
  // moving the whole page.
  const cardStyle = {
    background: "var(--white)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minHeight: 0,
  };
  const headerStyle = {
    padding: "14px 18px",
    borderBottom: "1px solid var(--border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexShrink: 0,
    background: "linear-gradient(180deg, #fdf8f2 0%, #fcf3e6 100%)",
  };
  const headerTitle = {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 20,
    fontWeight: 500,
    color: "#2c1f14",
  };
  const scrollBody = {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
  };
  const scrollClass = "cio-scroll";

  return (
    <div style={{
      // Hard-bound the workspace to the viewport so panels stay locked
      // inside their grid cells (no panel can overflow into another).
      display: "flex", flexDirection: "column", gap: 12,
      height: "calc(100vh - 130px)",
      minHeight: 520,
    }}>
      {/* Slim scrollbars on every internal scroll body so they don't shove
          their content sideways or look heavy against the cream panels. */}
      <style>{`
        .cio-scroll::-webkit-scrollbar           { width: 8px; }
        .cio-scroll::-webkit-scrollbar-track     { background: transparent; }
        .cio-scroll::-webkit-scrollbar-thumb     { background: rgba(180,140,80,.28); border-radius: 8px; }
        .cio-scroll::-webkit-scrollbar-thumb:hover { background: rgba(180,140,80,.45); }
      `}</style>

      {/* Toast — sticky-feel banner that doesn't push the layout. */}
      {msg.text && (
        <div style={{
          padding: "11px 16px", borderRadius: 8,
          background: msg.type === "success" ? "#eaf5ee" : "#fef0ee",
          color:      msg.type === "success" ? "#2d6a4f" : "#a93226",
          border:     `1px solid ${msg.type === "success" ? "#a8d5b5" : "#f5c5bc"}`,
          fontSize: 14, fontWeight: 500, flexShrink: 0,
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{ flex: 1 }}>
            {msg.type === "success" ? "✅" : "⚠️"} {msg.text}
          </span>
          {/* Walk-in just succeeded — surface a one-click receipt action
              so the front desk doesn't have to switch tabs to print. */}
          {msg.type === "success" && receiptHint && (
            <>
              <button
                className="btn-outline"
                style={{ padding: "5px 12px", fontSize: 11.5 }}
                onClick={() => openReceiptFor({ id: receiptHint.id }, setReceipt)}
              >
                🧾 Print receipt · {receiptHint.code}
              </button>
              <button
                onClick={() => setReceiptHint(null)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#6b5142", fontSize: 13 }}
                title="Dismiss"
              >✕</button>
            </>
          )}
        </div>
      )}

      {/* Printable receipt overlay — shared with Billing.  Mounted at the
          top of the workspace so it overlays any panel. */}
      {receipt && (
        <ReceiptDialog
          reservation={receipt.reservation}
          payment={receipt.payment}
          allPayments={receipt.allPayments}
          facility={facility}
          onClose={() => setReceipt(null)}
        />
      )}

      {/* Workspace grid — left walk-in, right column split into two stacked
          panels.  Heights are constrained so each panel scrolls inside
          itself instead of the page scrolling as a whole. */}
      <div style={{
        flex: 1, minHeight: 0,
        display: "grid",
        gap: 14,
        gridTemplateColumns: "minmax(0, 1.15fr) minmax(0, 1fr)",
        gridTemplateRows: "minmax(0, 1fr) minmax(0, 1fr)",
      }}>

        {/* ── WALK-IN (full-height left column) ─────────────────────── */}
        <div style={{ ...cardStyle, gridRow: "1 / 3" }}>
          <div style={headerStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18 }}>🛎️</span>
              <span style={headerTitle}>Walk-in Check In</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="badge badge-green" style={{ background: "rgba(74,124,89,.14)" }}>New guest</span>
              <span className="badge badge-gold" title="Today's date">{todayLabel}</span>
            </div>
          </div>

          {/* Form structure: a fixed-height column with a scroll body for
              the fields and a pinned footer for the submit button.  This
              keeps "✓ Confirm Check In" always visible at the bottom of
              the left panel instead of riding the scroll and bleeding
              into the In House column on shorter screens. */}
          <form onSubmit={submitWalkIn} style={{
            flex: 1, minHeight: 0,
            display: "flex", flexDirection: "column",
          }}>
            <div className={scrollClass} style={{ ...scrollBody, padding: "16px 18px" }}>
            {/* Guest Identity */}
            <div className="f-group">
              <label className="f-label">Guest Full Name *</label>
              <input className="f-input" placeholder="e.g. Kwame Mensah"
                value={walkIn.full_name}
                onChange={(e) => setWalkIn({ ...walkIn, full_name: e.target.value })}
                required autoFocus/>
            </div>

            <div className="form-grid">
              <div className="f-group">
                <label className="f-label">Phone *</label>
                {/* Phone is REQUIRED for walk-in check-in — backend rejects
                    without one, and the welcome SMS depends on it. */}
                <input className="f-input" type="tel" required
                  placeholder="0244 000 000"
                  value={walkIn.phone}
                  onChange={(e) => setWalkIn({ ...walkIn, phone: e.target.value })}/>
              </div>
              <div className="f-group">
                <label className="f-label">Email (optional)</label>
                <input className="f-input" type="email" placeholder="guest@email.com"
                  value={walkIn.email}
                  onChange={(e) => setWalkIn({ ...walkIn, email: e.target.value })}/>
              </div>
              <div className="f-group">
                <label className="f-label">ID Type</label>
                <select className="f-select" value={walkIn.id_type}
                  onChange={(e) => setWalkIn({ ...walkIn, id_type: e.target.value })}>
                  <option>Ghana Card</option>
                  <option>Passport</option>
                  <option>Driver's Licence</option>
                  <option>Voter's ID</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="f-group">
                <label className="f-label">ID Number</label>
                <input className="f-input" placeholder="ID number"
                  value={walkIn.id_number}
                  onChange={(e) => setWalkIn({ ...walkIn, id_number: e.target.value })}/>
              </div>
            </div>

            {/* Section break */}
            <div style={{ height: 1, background: "rgba(180,140,80,.15)", margin: "4px 0 14px" }}/>

            {/* Stay — booking-level fields (dates, mode, payment) live
                here; the rooms list lives in the MultiRoomEditor below. */}
            <div className="form-grid">
              {/* Billing mode toggle */}
              <div className="f-group" style={{ gridColumn: "1 / -1" }}>
                <label className="f-label">Billing Mode</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {BILLING_MODE_OPTIONS.map(m => {
                    const on = walkIn.billing_mode === m.id;
                    return (
                      <button key={m.id} type="button"
                        onClick={() => setWalkIn({ ...walkIn, billing_mode: m.id })}
                        style={{
                          flex: 1, minWidth: 90,
                          padding: "10px 12px", borderRadius: 8, cursor: "pointer",
                          fontFamily: "'Jost', sans-serif",
                          background: on ? "linear-gradient(135deg, #c8a05a, #a8814a)" : "transparent",
                          color: on ? "#1a110a" : "#4a3828",
                          border: `1.5px solid ${on ? "#a8814a" : "rgba(180,140,80,.3)"}`,
                          fontWeight: on ? 600 : 500,
                          letterSpacing: ".06em",
                          transition: "background .15s, color .15s",
                        }}>
                        <div style={{ fontSize: 12.5, textTransform: "uppercase" }}>{m.label}</div>
                        <div style={{ fontSize: 10.5, fontWeight: 400, opacity: .8, marginTop: 2 }}>{m.hint}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Guided stay window — fields shown depend on the billing
                  mode so a "Nightly" walk-in only edits the date + nights
                  (times pinned), "Hourly" edits start + hour count,
                  "Daily" edits start + day count (24-hour blocks). */}
              <StayWindowPicker
                mode={walkIn.billing_mode}
                checkIn={walkIn.check_in_at}
                checkOut={walkIn.check_out_at}
                onChange={({ check_in_at, check_out_at }) =>
                  setWalkIn((w) => ({ ...w, check_in_at, check_out_at }))
                }
              />
              <div className="f-group">
                <label className="f-label">Deposit (GH₵)</label>
                <input className="f-input" type="number" step="0.01" placeholder="0"
                  value={walkIn.paid_amount}
                  onChange={(e) => setWalkIn({ ...walkIn, paid_amount: e.target.value })}/>
              </div>
              <div className="f-group">
                <label className="f-label">Payment Method</label>
                <select className="f-select" value={walkIn.payment_method}
                  onChange={(e) => setWalkIn({ ...walkIn, payment_method: e.target.value })}>
                  {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>

            {/* Rooms — one walk-in can take several rooms under the same
                guest.  All rooms share the booking-level dates + mode. */}
            <div style={{ marginTop: 14 }}>
              <MultiRoomEditor
                rooms={availableRooms}
                roomTypes={roomTypes}
                value={walkIn.rooms}
                billingMode={walkIn.billing_mode}
                onChange={(next) => setWalkIn({ ...walkIn, rooms: next })}
                money={money}
              />
            </div>

            {/* Live billing preview — recomputes as the form changes. */}
            <div style={{
              marginTop: 4,
              padding: "12px 14px",
              borderRadius: 9,
              background: billingPreview.ok
                ? "linear-gradient(135deg, rgba(200,160,90,.10), rgba(200,160,90,.04))"
                : "rgba(192,57,43,.06)",
              border: `1px solid ${billingPreview.ok ? "rgba(200,160,90,.3)" : "rgba(192,57,43,.25)"}`,
              display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
            }}>
              <div>
                <div style={{ fontSize: 10.5, letterSpacing: ".14em", textTransform: "uppercase", color: "#6b5142", fontWeight: 500 }}>
                  Stay Total
                </div>
                <div style={{ fontSize: 12.5, color: "#4a3828", marginTop: 2 }}>
                  {billingPreview.label || "Enter dates to see total"}
                </div>
              </div>
              <div style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 22, color: "#2c1f14", fontWeight: 600,
              }}>
                {money(billingPreview.total)}
              </div>
            </div>

            {/* Deposit policy banner (only shows when the rule is on). */}
            <DepositHint total={billingPreview.total} paid={walkIn.paid_amount} />

            </div>

            {/* Pinned action footer — outside the scroll body so it never
                moves and never overflows into the right column. */}
            <div style={{
              flexShrink: 0,
              padding: "12px 18px",
              borderTop: "1px solid var(--border)",
              background: "linear-gradient(180deg, #fdf8f2 0%, #f8edd8 100%)",
            }}>
              {(() => {
                // Re-evaluate against the live total so the button enables
                // / disables in real time as the operator types.
                const ok = !depositGuard.enabled
                  || (Number(walkIn.paid_amount) || 0) >= Math.min(
                       Number(depositGuard.required) || 0,
                       Number(billingPreview.total)  || 0,
                     );
                return (
                  <button type="submit" className="btn-dark"
                    style={{ width: "100%", padding: "13px", fontSize: 12.5, letterSpacing: ".15em" }}
                    disabled={submitting || !ok}
                    title={ok ? undefined : "Collect the required deposit first"}>
                    {submitting ? "Checking in…"
                     : !ok      ? "🔒 Deposit required"
                                : "✓ Confirm Check In"}
                  </button>
                );
              })()}
            </div>
          </form>
        </div>

        {/* ── EXPECTED ARRIVALS (top-right, scrolls independently) ──── */}
        <div style={cardStyle}>
          <div style={headerStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18 }}>📥</span>
              <span style={headerTitle}>Expected Arrivals</span>
            </div>
            <span className="badge badge-gold">
              {loading ? "…" : `${expectedArrivals.length} reserved`}
            </span>
          </div>
          <div className={scrollClass} style={scrollBody}>
            {loading ? (
              <div style={{ padding: 22, color: "#6b5142" }}>Loading…</div>
            ) : expectedArrivals.length === 0 ? (
              <EmptyState icon="📭" text="No upcoming reservations."/>
            ) : (
              expectedArrivals.map(r => {
                const inIso = r.check_in_at || r.check_in_date;
                const isToday = String(inIso || "").slice(0, 10) === new Date().toISOString().slice(0, 10);
                return (
                  <ArrivalRow
                    key={r.id} r={r} isToday={isToday}
                    busy={!!busy[r.id]} fmtDateTime={fmtDateTime}
                    onCheckIn={() => checkIn(r)}
                  />
                );
              })
            )}
          </div>
        </div>

        {/* ── IN HOUSE / DUE OUT (bottom-right, scrolls independently) ── */}
        <div style={cardStyle}>
          <div style={headerStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18 }}>🛏️</span>
              <span style={headerTitle}>In House — Due to Check Out</span>
            </div>
            <span className="badge badge-gold">
              {loading ? "…" : `${inHouse.length} ${inHouse.length === 1 ? "guest" : "guests"}`}
            </span>
          </div>
          <div className={scrollClass} style={scrollBody}>
            {loading ? (
              <div style={{ padding: 22, color: "#6b5142" }}>Loading…</div>
            ) : inHouse.length === 0 ? (
              <EmptyState icon="🌙" text="No guests currently in house."/>
            ) : (
              inHouse.map(g => (
                <InHouseRow
                  key={g.id} g={g}
                  due={isDueOut(g.check_out_at || g.check_out_date)}
                  busy={!!busy[g.id]} fmtDateTime={fmtDateTime} money={money}
                  now={now} isSuperAdmin={isSuperAdmin}
                  onCheckOut={() => openCheckout(g)}
                  onCancel={() => cancelStay(g)}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Checkout modal ──────────────────────────────────────────────── */}
      {checkoutModal && (
        <div className="modal-overlay" onClick={() => setCheckoutModal(null)}>
          <div className="modal-box" style={{ width: "min(480px, 92vw)" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Check Out — {checkoutModal.reservation.guest_name}</span>
              <button className="modal-close" onClick={() => setCheckoutModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ background: "rgba(200,160,90,.08)", border: "1px solid rgba(200,160,90,.25)", borderRadius: 8, padding: "14px 16px", marginBottom: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: "#6b5142" }}>Total</span>
                  <span style={{ fontWeight: 500 }}>{money(checkoutModal.reservation.total_amount)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: "#6b5142" }}>Paid so far</span>
                  <span style={{ fontWeight: 500 }}>{money(checkoutModal.reservation.paid_amount)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, borderTop: "1px solid rgba(180,140,80,.25)" }}>
                  <span style={{ fontSize: 14, color: "#2c1f14", fontWeight: 500 }}>Balance due</span>
                  <span style={{ fontWeight: 600, color: Number(checkoutModal.reservation.balance) > 0 ? "#a93226" : "#2d6a4f" }}>
                    {money(checkoutModal.reservation.balance)}
                  </span>
                </div>
              </div>

              <div className="form-grid">
                <div className="f-group">
                  <label className="f-label">Amount to settle (GH₵)</label>
                  <input className="f-input" type="number" step="0.01"
                    value={checkoutModal.payment_amount}
                    onChange={(e) => setCheckoutModal({ ...checkoutModal, payment_amount: e.target.value })}/>
                </div>
                <div className="f-group">
                  <label className="f-label">Method</label>
                  <select className="f-select" value={checkoutModal.payment_method}
                    onChange={(e) => setCheckoutModal({ ...checkoutModal, payment_method: e.target.value })}>
                    {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div className="f-group">
                <label className="f-label">Notes</label>
                <textarea className="f-input" rows={2} placeholder="Optional checkout note…"
                  value={checkoutModal.notes}
                  onChange={(e) => setCheckoutModal({ ...checkoutModal, notes: e.target.value })}/>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                <button className="btn-dark" style={{ flex: 1 }}
                  onClick={confirmCheckout} disabled={busy[checkoutModal.reservation.id]}>
                  {busy[checkoutModal.reservation.id] ? "Processing…" : "✓ Confirm Check Out"}
                </button>
                <button className="btn-outline" onClick={() => setCheckoutModal(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Row + state helpers (kept here to keep the main component readable)
// ─────────────────────────────────────────────────────────────────────────────

function ArrivalRow({ r, isToday, busy, fmtDateTime, onCheckIn }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "12px 16px",
      borderBottom: "1px solid rgba(180,140,80,.1)",
      transition: "background .15s",
    }}
      onMouseEnter={(e) => e.currentTarget.style.background = "rgba(200,160,90,.04)"}
      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
    >
      <div className="avatar" style={{ width: 36, height: 36, fontSize: 16 }}>
        {(r.guest_name || "?").charAt(0).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, color: "#2c1f14", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {r.guest_name}
        </div>
        <div style={{ fontSize: 12, color: "#6b5142", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <strong style={{ color: "#8a6a30" }}>{r.code}</strong> · Room {r.room_number || "—"} · {fmtDateTime(r.check_in_at || r.check_in_date)} → {fmtDateTime(r.check_out_at || r.check_out_date)}
          {r.billing_mode && r.billing_mode !== "nightly" && (
            <span style={{ marginLeft: 6, fontSize: 10, color: "#8a6a30", textTransform: "uppercase", letterSpacing: ".08em" }}>· {r.billing_mode}</span>
          )}
        </div>
      </div>
      {isToday && <span className="badge badge-green" style={{ fontSize: 10.5 }}>today</span>}
      <button
        className="btn-gold"
        style={{ padding: "7px 13px", fontSize: 11 }}
        onClick={onCheckIn}
        disabled={busy}
      >
        {busy ? "…" : "Check In"}
      </button>
    </div>
  );
}

// Formats a millisecond delta as HH:MM:SS, with a leading minus when
// the deadline has already passed.
function fmtCountdown(ms) {
  const sign = ms < 0 ? "−" : "";
  const abs = Math.abs(Math.floor(ms / 1000));
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  const p = (n) => String(n).padStart(2, "0");
  return `${sign}${p(h)}:${p(m)}:${p(s)}`;
}

function InHouseRow({ g, due, busy, fmtDateTime, money, now, isSuperAdmin, onCheckOut, onCancel }) {
  // ── Live countdowns ────────────────────────────────────────────────
  // Time remaining (or overdue) until the scheduled checkout.
  const outMs   = new Date(g.check_out_at || g.check_out_date).getTime();
  const toOutMs = outMs - now;
  const isOverdue = toOutMs <= 0;

  // Time remaining in the 30-minute "regular user can still cancel"
  // window after actual check-in.  A null actual_check_in_at means we
  // somehow got here without recording a check-in time — treat as expired
  // to be safe.
  const checkedInMs   = g.actual_check_in_at ? new Date(g.actual_check_in_at).getTime() : null;
  const cancelDeadlineMs = checkedInMs ? checkedInMs + CANCEL_WINDOW_MS : 0;
  const cancelLeftMs  = cancelDeadlineMs - now;
  const inCancelWindow = cancelLeftMs > 0;
  const canCancel = inCancelWindow || isSuperAdmin;

  // Color the checkout countdown amber as it approaches the BEEP_WINDOW
  // and red once we're inside the last 5 minutes or overdue.
  const minsToOut = toOutMs / 60000;
  const outColor =
    isOverdue              ? "#a93226" :
    minsToOut <= 5         ? "#a93226" :
    minsToOut <= BEEP_WINDOW_MIN ? "#a85618" :
                              "#2d6a4f";

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "12px 16px",
      borderBottom: "1px solid rgba(180,140,80,.1)",
      transition: "background .15s",
    }}
      onMouseEnter={(e) => e.currentTarget.style.background = "rgba(200,160,90,.04)"}
      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
    >
      <div className="avatar" style={{ width: 38, height: 38, fontSize: 16 }}>
        {(g.guest_name || "?").charAt(0).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, color: "#2c1f14", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {g.guest_name}
        </div>
        <div style={{ fontSize: 12, color: "#6b5142", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <strong style={{ color: "#8a6a30" }}>{g.code}</strong> · Room {g.room_number} · Out {fmtDateTime(g.check_out_at || g.check_out_date)}
          {g.billing_mode && g.billing_mode !== "nightly" && (
            <span style={{ marginLeft: 6, fontSize: 10, color: "#8a6a30", textTransform: "uppercase", letterSpacing: ".08em" }}>· {g.billing_mode}</span>
          )}
        </div>
        {/* Live countdowns — checkout + cancel window. */}
        <div style={{ display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
          <span title="Time remaining until scheduled checkout"
                style={{
                  fontSize: 11.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  color: outColor, fontWeight: 500,
                  background: "rgba(200,160,90,.08)", padding: "2px 8px", borderRadius: 6,
                }}>
            {isOverdue ? "⏰ Overdue " : "⏱ Checkout in "}{fmtCountdown(toOutMs)}
          </span>
          {inCancelWindow ? (
            <span title="Regular staff can still cancel the check-in"
                  style={{
                    fontSize: 11.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    color: "#6b5142", fontWeight: 500,
                    background: "rgba(180,140,80,.10)", padding: "2px 8px", borderRadius: 6,
                  }}>
              Cancel window {fmtCountdown(cancelLeftMs)}
            </span>
          ) : (
            <span title="Only a super admin can cancel after the 30-minute window"
                  style={{
                    fontSize: 11.5, color: "#8a3226", fontWeight: 500,
                    background: "rgba(192,57,43,.06)",
                    border: "1px solid rgba(192,57,43,.18)",
                    padding: "2px 8px", borderRadius: 6,
                  }}>
              🔒 Super admin only
            </span>
          )}
        </div>
      </div>
      <div style={{ textAlign: "right", marginRight: 4 }}>
        <div style={{
          fontFamily: "'Cormorant Garamond', serif", fontSize: 17,
          color: Number(g.balance) > 0 ? "#a93226" : "#2d6a4f",
          fontWeight: 500, lineHeight: 1.1,
        }}>
          {money(g.balance)}
        </div>
        <span className={`badge ${due ? "badge-red" : "badge-green"}`} style={{ fontSize: 10, marginTop: 3, display: "inline-block" }}>
          {due ? "due out" : "in house"}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <button className="btn-dark" style={{ padding: "8px 14px", fontSize: 11 }}
          onClick={onCheckOut} disabled={busy}>
          {busy ? "…" : "Check Out"}
        </button>
        <button
          onClick={onCancel}
          disabled={busy || !canCancel}
          title={canCancel
            ? (inCancelWindow ? "Cancel within the 30-minute window" : "Super admin cancel")
            : "Cancel window expired — super admin only"}
          style={{
            padding: "6px 12px", fontSize: 10.5,
            background: "transparent",
            border: `1.5px solid ${canCancel ? "rgba(192,57,43,.35)" : "rgba(180,140,80,.2)"}`,
            borderRadius: 6,
            color: canCancel ? "#a93226" : "#b8a090",
            cursor: canCancel ? "pointer" : "not-allowed",
            fontFamily: "'Jost', sans-serif", letterSpacing: ".08em",
            textTransform: "uppercase", fontWeight: 500,
            opacity: canCancel ? 1 : 0.7,
          }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div style={{
      padding: "40px 20px",
      textAlign: "center",
      color: "#8a7060",
    }}>
      <div style={{ fontSize: 36, marginBottom: 10, opacity: .55 }}>{icon}</div>
      <div style={{ fontSize: 13.5 }}>{text}</div>
    </div>
  );
}
